// src/pages/ContactPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { fetchContacts } from "../Api";

function normalizePhoneForWa(phone) {
  // "+90 507..." / "0535..." / "535..." -> "90535..."
  let digits = (phone || "").replace(/[^\d]/g, "");
  if (!digits) return "";
  if (digits.startsWith("0") && digits.length === 11) digits = `90${digits.slice(1)}`;
  else if (digits.startsWith("5") && digits.length === 10) digits = `90${digits}`;
  return digits;
}

function normalizePhoneForTel(phone) {
  const raw = (phone || "").trim();
  if (!raw) return "";
  if (raw.startsWith("+")) {
    return `+${raw.slice(1).replace(/[^\d]/g, "")}`;
  }
  const digits = raw.replace(/[^\d]/g, "");
  if (digits.startsWith("0") && digits.length >= 10) return `+90${digits.slice(1)}`;
  if (digits.startsWith("5") && digits.length === 10) return `+90${digits}`;
  if (digits.startsWith("90")) return `+${digits}`;
  return digits;
}

/** TR cep: ulusal numara 5 ile baslar (905xx / 05xx / 5xx) */
function isTurkishMobile(phone) {
  const digits = (phone || "").replace(/[^\d]/g, "");
  if (!digits) return false;
  if (digits.startsWith("90") && digits.length >= 12) return digits.charAt(2) === "5";
  if (digits.startsWith("0") && digits.length >= 11) return digits.charAt(1) === "5";
  return digits.startsWith("5");
}

const btnBase = {
  height: 40,
  minWidth: 88,
  padding: "0 14px",
  borderRadius: 10,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  textDecoration: "none",
  fontWeight: 800,
  fontSize: 13,
  boxSizing: "border-box",
};

function CallButton({ phone }) {
  const telHref = `tel:${normalizePhoneForTel(phone)}`;
  return (
    <a
      href={telHref}
      title="Ara"
      style={{
        ...btnBase,
        background: "#0f172a",
        color: "#fff",
        boxShadow: "0 8px 18px rgba(15,23,42,0.12)",
      }}
    >
      Ara
    </a>
  );
}

function WhatsAppButton({ phone }) {
  const wa = useMemo(() => {
    const digits = normalizePhoneForWa(phone);
    return `https://wa.me/${digits}`;
  }, [phone]);

  return (
    <a
      href={wa}
      target="_blank"
      rel="noreferrer"
      title="WhatsApp"
      style={{
        ...btnBase,
        background: "#25D366",
        color: "#fff",
        boxShadow: "0 8px 18px rgba(37,211,102,0.28)",
      }}
    >
      <svg width="18" height="18" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path
          d="M16 3C9.372 3 4 8.07 4 14.318c0 2.42.83 4.66 2.248 6.47L5 29l8.54-1.87A12.66 12.66 0 0 0 16 25.636c6.628 0 12-5.07 12-11.318C28 8.07 22.628 3 16 3Z"
          fill="#fff"
        />
        <path
          d="M12.29 10.9c.23-.53.47-.54.68-.55h.58c.19 0 .45.07.69.53.23.47.79 1.6.86 1.72.07.13.12.28.02.45-.09.17-.14.28-.28.43-.14.15-.29.33-.41.44-.14.13-.29.27-.13.53.16.27.7 1.12 1.5 1.82 1.03.9 1.9 1.18 2.17 1.31.27.13.43.11.59-.07.16-.18.68-.75.86-1.01.18-.26.36-.21.6-.12.24.09 1.53.69 1.79.82.26.13.43.2.49.31.06.11.06.64-.15 1.26-.21.62-1.22 1.2-1.68 1.27-.42.07-.96.1-1.55-.09-.36-.11-.82-.26-1.4-.5-2.46-1.01-4.07-3.36-4.19-3.52-.12-.16-1-1.32-1-2.52 0-1.2.64-1.79.86-2.04Z"
          fill="#25D366"
        />
      </svg>
      WhatsApp
    </a>
  );
}

function ContactCard({ item }) {
  const showWhatsApp = isTurkishMobile(item.phone);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid rgba(15,23,42,0.10)",
        borderRadius: 12,
        padding: "18px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        boxShadow: "0 12px 28px rgba(2,6,23,0.06)",
      }}
    >
      <div style={{ fontWeight: 900, fontSize: 17, color: "#0f172a", textAlign: "center" }}>
        {item.name}
      </div>

      <div style={{ fontWeight: 700, color: "#64748b", fontSize: 14 }}>{item.phone}</div>

      <div
        style={{
          marginTop: 8,
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          justifyContent: "center",
        }}
      >
        <CallButton phone={item.phone} />
        {showWhatsApp ? <WhatsAppButton phone={item.phone} /> : null}
      </div>
    </div>
  );
}

export default function ContactsPage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const data = await fetchContacts();
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        setErr(e?.message || "İletişim listesi alınamadı.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#f6f7fb", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 1100 }}>
        <div style={{ fontSize: 14, color: "#334155", marginBottom: 14 }}>
          Yöneticinize mesaj veya soru gönderin
        </div>

        {err ? (
          <div
            style={{
              background: "#fee2e2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              padding: 12,
              borderRadius: 12,
              fontWeight: 900,
              marginBottom: 14,
            }}
          >
            {err}
          </div>
        ) : null}

        {loading ? (
          <div style={{ color: "#475569", fontWeight: 800 }}>Yükleniyor…</div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 18,
              alignItems: "start",
            }}
          >
            {items.map((it, idx) => (
              <ContactCard key={`${it.phone}-${idx}`} item={it} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
